/**
 * Return Reports List Page
 *
 * Displays all return reports for a merchant with filtering and search.
 * Nested under Bookings section.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  X,
  MapPin,
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  ImageIcon,
  Package,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { getReturnReports } from '../services/return-reports.service';
import { getLocations } from '../services/locations.service';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import type { ReturnReportListItem, ReturnReportFilters, OverallStatus } from '../types/return-reports';
import { STATUS_CONFIG, REVIEW_STATUS_CONFIG } from '../types/return-reports';
import type { Database } from '../lib/database.types';

type Location = Database['public']['Tables']['locations']['Row'];

export default function ReturnReportsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { formatDate } = useDateFormatter();

  // Data state
  const [reports, setReports] = useState<ReturnReportListItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OverallStatus | 'all'>('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState<'pending' | 'reviewed' | 'all'>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const ITEMS_PER_PAGE = 20;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Load data on mount and filter changes
  useEffect(() => {
    loadData();
  }, [merchantId, locationIds, currentPage]);

  // Reload when filters change (with debounce for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadData();
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [statusFilter, reviewStatusFilter, locationFilter, dateFrom, dateTo, searchQuery]);

  async function loadData() {
    if (!merchantId) return;

    setLoading(true);
    setError(null);

    // Load locations for filter dropdown
    if (locations.length === 0) {
      const { data: locationsData } = await getLocations(
        { merchantId, locationIds, isPlatformAdmin },
        { active: true }
      );
      if (locationsData) {
        setLocations(locationsData);
      }
    }

    // Build filters
    const filters: ReturnReportFilters = {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      reviewStatus: reviewStatusFilter !== 'all' ? reviewStatusFilter : undefined,
      locationId: locationFilter !== 'all' ? locationFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      search: searchQuery || undefined,
    };

    // Load return reports
    const { data, total, error: fetchError } = await getReturnReports(
      merchantId,
      locationIds,
      filters,
      currentPage
    );

    if (fetchError) {
      setError(fetchError);
    } else {
      setReports(data);
      setTotalCount(total);
    }

    setLoading(false);
  }

  const clearFilters = () => {
    setStatusFilter('all');
    setReviewStatusFilter('all');
    setLocationFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || reviewStatusFilter !== 'all' ||
    locationFilter !== 'all' || dateFrom || dateTo || searchQuery;

  const handleRowClick = (report: ReturnReportListItem) => {
    // Navigate to booking detail with return info tab open
    navigate(`/bookings/${report.booking_id}/edit?tab=returnInfo`);
  };

  return (
    <div className="space-y-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('returnReports.title')}</h1>
          <p className="text-sm text-secondary mt-xs">
            {t('returnReports.subtitle')}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-md">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('returnReports.searchPlaceholder')}
                className="input pl-11 w-full"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Quick Filters */}
          <div className="flex items-center gap-sm">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OverallStatus | 'all')}
              className="input text-sm"
            >
              <option value="all">{t('returnReports.allStatus')}</option>
              <option value="all_ok">{t('returnReports.allOk')}</option>
              <option value="issues_reported">{t('returnReports.issuesReported')}</option>
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-xs`}
            >
              <Filter className="w-4 h-4" />
              {t('common.filter')}
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-blue" />
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn btn-secondary text-sm"
              >
                {t('returnReports.clear')}
              </button>
            )}
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-md pt-md border-t border-primary grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-md">
            <div>
              <label className="block text-xs font-medium text-secondary mb-xs">
                {t('returnReports.reviewStatus')}
              </label>
              <select
                value={reviewStatusFilter}
                onChange={(e) => setReviewStatusFilter(e.target.value as any)}
                className="input w-full text-sm"
              >
                <option value="all">{t('common.all')}</option>
                <option value="pending">{t('returnReports.pendingReview')}</option>
                <option value="reviewed">{t('returnReports.reviewed')}</option>
              </select>
            </div>

            {locations.length > 1 && (
              <div>
                <label className="block text-xs font-medium text-secondary mb-xs">
                  {t('returnReports.location')}
                </label>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="input w-full text-sm"
                >
                  <option value="all">{t('returnReports.allLocations')}</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-secondary mb-xs">
                {t('returnReports.fromDate')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input w-full text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary mb-xs">
                {t('returnReports.toDate')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input w-full text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary">
          {t('returnReports.reportsFound', { count: totalCount })}
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-danger/10 border-danger/20">
          <div className="flex items-start gap-md">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
            <div>
              <p className="font-medium text-danger">{t('returnReports.errorLoading')}</p>
              <p className="text-sm text-danger/80 mt-xs">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="card flex items-center justify-center py-xl">
          <Loader2 className="w-8 h-8 animate-spin text-blue" />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && reports.length === 0 && (
        <div className="card text-center py-xl">
          <Package className="w-12 h-12 text-secondary mx-auto mb-md" />
          <h3 className="text-lg font-medium text-primary mb-sm">{t('returnReports.noReports')}</h3>
          <p className="text-sm text-secondary">
            {hasActiveFilters
              ? t('returnReports.tryAdjusting')
              : t('returnReports.emptyHint')}
          </p>
        </div>
      )}

      {/* Reports Table */}
      {!loading && !error && reports.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.orderCustomer')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.location')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.status')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.preview')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.date')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.review')}
                  </th>
                  <th className="px-md py-sm text-left text-xs font-medium text-secondary uppercase tracking-wider">
                    {t('returnReports.notes')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary">
                {reports.map((report) => {
                  const statusConfig = STATUS_CONFIG[report.overall_status];
                  const reviewConfig = REVIEW_STATUS_CONFIG[report.review_status || 'pending'];

                  return (
                    <tr
                      key={report.return_id}
                      onClick={() => handleRowClick(report)}
                      className="hover:bg-secondary/50 cursor-pointer transition-colors"
                    >
                      <td className="px-md py-md">
                        <div>
                          <p className="font-medium text-primary">{report.order_id}</p>
                          <p className="text-sm text-secondary">
                            {report.customer_firstname} {report.customer_lastname}
                          </p>
                        </div>
                      </td>
                      <td className="px-md py-md">
                        <div className="flex items-center gap-xs text-sm text-secondary">
                          <MapPin className="w-4 h-4" />
                          {report.location_name}
                        </div>
                      </td>
                      <td className="px-md py-md">
                        <span className={`inline-flex items-center gap-xs px-sm py-xs rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                          {report.overall_status === 'all_ok' ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : (
                            <AlertTriangle className="w-3 h-3" />
                          )}
                          {t(statusConfig.labelKey)}
                        </span>
                      </td>
                      <td className="px-md py-md">
                        {report.first_photo_url ? (
                          <div className="relative w-10 h-10">
                            <img
                              src={report.first_photo_url}
                              alt="Preview"
                              className="w-10 h-10 rounded object-cover"
                            />
                            {report.booking_photo_count > 1 && (
                              <span className="absolute -top-1 -right-1 bg-blue text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                                +{report.booking_photo_count - 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded bg-secondary flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-secondary" />
                          </div>
                        )}
                      </td>
                      <td className="px-md py-md">
                        <div className="text-sm text-secondary">
                          {formatDate(report.created_at_vienna || report.created_at)}
                        </div>
                      </td>
                      <td className="px-md py-md">
                        <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${reviewConfig.bgColor} ${reviewConfig.color}`}>
                          {t(reviewConfig.labelKey)}
                        </span>
                      </td>
                      <td className="px-md py-md">
                        {report.internal_notes ? (
                          <span className="text-sm text-secondary" title={report.internal_notes}>
                            {report.internal_notes.length > 40
                              ? `${report.internal_notes.substring(0, 40)}...`
                              : report.internal_notes}
                          </span>
                        ) : (
                          <span className="text-sm text-secondary/50">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-md py-sm border-t border-primary flex items-center justify-between">
              <p className="text-sm text-secondary">
                {t('returnReports.pageOf', { current: currentPage, total: totalPages })}
              </p>
              <div className="flex items-center gap-sm">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary btn-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary btn-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
